import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import { League } from '../../../sdk/model';
import LeagueEventDisplay from '../leagueEvent/LeagueEventDisplay';
import { leagueEventApi } from '../../../adapters/APIExporter';

const PAGE_SIZE = 10;

interface RecentActivitiesProps {
  league: League;
  playerMap: Map<string | undefined, any>;
  teamMap: Map<string | undefined, any>;
}

const RecentActivities: React.FC<RecentActivitiesProps> = ({
  league,
  playerMap,
  teamMap,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [allHistoryLoaded, setAllHistoryLoaded] = useState(false);
  const [events, setEvents] = useState(league.recentEvents || []);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  React.useEffect(() => {
    const newEvents = [...events];
    league.recentEvents?.forEach(event => {
      const matchingEvent = newEvents.find(newEvent => newEvent.id === event.id);
      if (matchingEvent) {
        matchingEvent.reactions = event.reactions;
      }
    })
    setEvents(newEvents);
  }, [league]);
  
  function loadHistory() {
    setIsLoading(true);
    leagueEventApi.getLeagueEvents(league.id, events ? events.length : 0).then(response => {
      const newEvents = response.data;
      setEvents(prevEvents => [...prevEvents, ...newEvents]);
      if(newEvents.length < PAGE_SIZE) {
        setAllHistoryLoaded(true);
      }
      setIsLoading(false);
    });
  }
  
  useEffect(() => {
    loadHistory();
  }, []);

  if (!events.length) {
    return (
      <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
        No recent activities to display.
      </Typography>
    );
  }

  // Mobile layout - Direct rendering of cards
  if (isMobile) {
    return (
      <Paper 
        sx={{ 
          maxHeight: '800px',
          overflow: 'auto',
          p: 1,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: theme.palette.grey[100],
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: theme.palette.grey[400],
            borderRadius: '4px',
            '&:hover': {
              background: theme.palette.grey[500],
            },
          },
          scrollbarWidth: 'thin',
          scrollbarColor: `${theme.palette.grey[400]} ${theme.palette.grey[100]}`,
        }}
      >
        {events.map((event) => (
          <LeagueEventDisplay
            key={event.id}
            event={event}
            playerMap={playerMap}
            teamMap={teamMap}
          />
        ))}
        <div style={{ textAlign: 'center', padding: '16px' }}>
          {allHistoryLoaded ? (
            <Typography variant="body2" color="text.secondary">
              Beginning of League History
            </Typography>
          ) : (
            <Button 
              onClick={loadHistory} 
              disabled={isLoading}
              variant="outlined"
              fullWidth
            >
              {isLoading ? 'Loading...' : 'Load More'}
            </Button>
          )}
        </div>
      </Paper>
    );
  }

  // Desktop layout - Table structure
  return (
    <TableContainer 
      component={Paper} 
      sx={{ 
        maxHeight: '800px',
        overflow: 'auto',
        '&::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: theme.palette.grey[100],
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: theme.palette.grey[400],
          borderRadius: '4px',
          '&:hover': {
            background: theme.palette.grey[500],
          },
        },
        scrollbarWidth: 'thin',
        scrollbarColor: `${theme.palette.grey[400]} ${theme.palette.grey[100]}`,
      }}
    >
      <Table stickyHeader size="medium">
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Event Type</TableCell>
            <TableCell>Details</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {events.map((event) => (
            <LeagueEventDisplay
              key={event.id}
              event={event}
              playerMap={playerMap}
              teamMap={teamMap}
            />
          ))}
          {allHistoryLoaded ? (
            <TableRow>
              <TableCell colSpan={3} align="center">
                <Typography variant="body2" color="text.secondary">
                  Beginning of League History
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            <TableRow>
              <TableCell colSpan={3} align="center">
                <Button 
                  onClick={loadHistory} 
                  disabled={isLoading}
                >
                  {isLoading ? 'Loading...' : 'Load More'}
                </Button>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default RecentActivities;
